// @ts-nocheck
import { getCookie } from "@insite/client-framework/Common/Cookies";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import resetReCaptcha from "@insite/client-framework/Store/Components/ReCaptcha/Handlers/ResetReCaptcha";
import validateReCaptcha from "@insite/client-framework/Store/Components/ReCaptcha/Handlers/ValidateReCaptcha";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { RefObject, useEffect, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

/* Starter Custom Code - Start */
import { sizeVariantValues } from "@insite/mobius/FormField/formStyles";
import breakpointValues from "@toolkit/Framework/Common/Utilities/breakpointValues";
import cssProp from "@toolkit/Framework/Common/Utilities/cssProp";
import { useCurrentLanguage } from "@toolkit/Framework/Store/Context/ContextHooks";

const sizeVariant = sizeVariantValues.default as any;
/* Starter Custom Code - End */

interface OwnProps {
    location: string;
    recaptchaRef: RefObject<ReCAPTCHA>;
    extendedStyles?: ReCaptchaStyles;
}

const mapStateToProps = (state: ApplicationState, { location, recaptchaRef }: OwnProps) => {
    const { websiteSettings } = getSettingsCollection(state);
    const session = getSession(state);
    const isEnabledSetting = (websiteSettings as { [key: string]: any })[`reCaptchaEnabledFor${location}`]; // Starter Custom Code
    return {
        siteKey: websiteSettings.reCaptchaSiteKey,
        isEnabled: typeof isEnabledSetting === "undefined" || isEnabledSetting, // Starter Custom Code
        isAuthenticated: session && (session.isAuthenticated || session.rememberMe) && !session.isGuest,
        errorMessage: state.components.reCaptcha.errorMessage[location],
        recaptchaRef,
        location,
    };
};

const mapDispatchToProps = {
    validateReCaptcha,
    resetReCaptcha,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ReCaptchaStyles {
    wrapper?: InjectableCss;
    errorMessageText?: TypographyPresentationProps;
}

export const reCaptchaStyles: ReCaptchaStyles = {
    wrapper: {
        css: css`
            ${x => breakpointValues(x.theme.design.form.grid.base.vertical, ["margin-top", "margin-bottom"])}

            &:first-child {
                margin-top: 0;
            }

            &:last-child {
                margin-bottom: 0;
            }
        `,
    },
    errorMessageText: {
        color: "danger",
        css: css`
            ${() => cssProp("font-size", sizeVariant.descriptionFontSize, x => `${x}px`)}
            ${() => cssProp("line-height", sizeVariant.descriptionLineHeight)}
            ${() => cssProp("margin-top", sizeVariant.descriptionLineSpacing, x => `${x}px`)}
            font-weight: ${x => x.theme.design.typography.weight.bold};
        `,
    },
};

const ReCaptcha = ({
    siteKey,
    isEnabled,
    isAuthenticated,
    errorMessage,
    recaptchaRef,
    location,
    validateReCaptcha,
    resetReCaptcha,
    extendedStyles,
}: Props) => {
    const [styles] = useState(() => mergeToNew(reCaptchaStyles, extendedStyles));
    const [firstRender, setFirstRender] = useState(true);
    /* Starter Custom Code - Start */
    const languageCode = useCurrentLanguage()?.languageCode;
    const language = languageCode ? languageCode.slice(0, 2) : undefined;
    /* Starter Custom Code - End */

    useEffect(() => {
        setFirstRender(false);
        resetReCaptcha({ location });
    }, []);

    const handleSuccess = () => {
        validateReCaptcha({ location, recaptchaRef });
    };

    if (firstRender || !isEnabled || isAuthenticated || !!getCookie("g-recaptcha-verified")) {
        return null;
    }

    return (
        <StyledWrapper {...styles.wrapper}>
            <ReCAPTCHA
                ref={recaptchaRef}
                sitekey={siteKey}
                onChange={handleSuccess}
                // Starter Custom Code
                hl={language}
            />
            {errorMessage && (
                <Typography as="p" {...styles.errorMessageText}>
                    {errorMessage}
                </Typography>
            )}
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ReCaptcha);
